import type {
  DocumentDownloadFormat,
  DocumentDownloadTarget,
  DocumentItem,
} from '@/js/account/entities/document/model/types';
import { accountDocumentsDownloadsStore } from '@/js/account/entities/document/api';
import { trackFileDownload } from '@/js/lib/analytics';

export const DOCUMENT_DOWNLOAD_FORMAT_LABELS: Record<
  DocumentDownloadFormat,
  string
> = {
  pdf: 'Download as PDF',
  docx: 'Download as DOCX',
};

export const DEFAULT_DOCUMENT_READ_MINUTES = 4;

export function formatDocumentReadTime(readMinutes: number | null | undefined): string {
  return `${resolveReadMinutes(readMinutes)} min`;
}

export function normalizeReadMinutes(
  value: number | null | undefined,
): number | null {
  if (value == null) {
    return null;
  }

  const minutes = Number(value);

  if (!Number.isFinite(minutes) || minutes < 1) {
    return null;
  }

  return Math.round(minutes);
}

export function resolveReadMinutes(
  value: number | null | undefined,
): number {
  return normalizeReadMinutes(value) ?? DEFAULT_DOCUMENT_READ_MINUTES;
}

function normalizeDownloadUrl(url: string | null | undefined): string | null {
  if (url == null || url.trim() === '') {
    return null;
  }

  return url;
}

export function getDocumentDownloadFormats(
  document: Pick<DocumentItem, 'pdfUrl' | 'docxUrl'>,
): DocumentDownloadFormat[] {
  const formats: DocumentDownloadFormat[] = [];

  if (normalizeDownloadUrl(document.pdfUrl) !== null) {
    formats.push('pdf');
  }

  if (normalizeDownloadUrl(document.docxUrl) !== null) {
    formats.push('docx');
  }

  return formats;
}

export function hasDocumentDownloads(
  document: Pick<DocumentItem, 'pdfUrl' | 'docxUrl'>,
): boolean {
  return getDocumentDownloadFormats(document).length > 0;
}

export function getDocumentDownloadUrl(
  document: Pick<DocumentItem, 'pdfUrl' | 'docxUrl'>,
  format?: DocumentDownloadFormat,
): string | null {
  if (format === 'pdf') {
    return normalizeDownloadUrl(document.pdfUrl);
  }

  if (format === 'docx') {
    return normalizeDownloadUrl(document.docxUrl);
  }

  return normalizeDownloadUrl(document.pdfUrl)
    ?? normalizeDownloadUrl(document.docxUrl);
}

function triggerBrowserDownload(url: string): void {
  const link = window.document.createElement('a');

  link.href = url;
  link.download = '';
  link.rel = 'noopener';
  link.style.display = 'none';

  window.document.body.append(link);
  link.click();
  link.remove();
}

export async function downloadDocument(
  document: DocumentDownloadTarget,
  format: DocumentDownloadFormat,
): Promise<void> {
  const url = getDocumentDownloadUrl(document, format);

  if (url === null) {
    return;
  }

  await accountDocumentsDownloadsStore({ documentId: document.id });

  trackFileDownload({
    format,
    title: document.title,
    documentId: document.id,
  });

  triggerBrowserDownload(url);
}
